// src/features/localization/PlatformConfigSettings.tsx
// Admin: default locale, available locales, fallback chain editor (ordered
// list), auto-detect / allow-override toggles.

import { useEffect, useState } from 'react';
import { useI18n, useLocalization } from '@react/hooks';
import { Alert, Button } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { PlatformLocaleConfig } from '@core/types/localization';
import { errMessage } from './helpers';

export function PlatformConfigSettings() {
  const { t } = useI18n();
  const store = useLocalization();

  const [config, setConfig] = useState<PlatformLocaleConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addLocale, setAddLocale] = useState('');
  const [addFallback, setAddFallback] = useState('');

  const load = () => {
    setIsLoading(true);
    setError(null);
    store
      .GetPlatformLocaleConfig()
      .then((res) => setConfig(res.config ?? null))
      .catch((e: unknown) => setError(errMessage(e, t('localization.config.loadError'))))
      .finally(() => setIsLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch: Partial<PlatformLocaleConfig>) => {
    if (!config) return;
    setBusy(true);
    setError(null);
    try {
      const res = await store.UpdatePlatformLocaleConfig({ config: { ...config, ...patch } });
      if (res.config) setConfig(res.config);
    } catch (e) {
      setError(errMessage(e, t('localization.config.saveError')));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <div className="h-48 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  if (!config) return <Alert variant="error">{error || t('localization.config.notFound')}</Alert>;

  const availableLocales = store.items.length > 0 ? store.items.map((l) => l.code) : config.available_locales;

  return (
    <div className="max-w-xl space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <div>
        <p className="mb-1 text-sm text-muted-foreground">{t('localization.config.defaultLocale')}</p>
        <Select
          value={config.default_locale}
          onChange={(e) => save({ default_locale: e.target.value })}
          options={config.available_locales.map((c) => ({ value: c, label: c }))}
          aria-label={t('localization.config.defaultLocale')}
          disabled={busy}
        />
      </div>

      <div>
        <p className="mb-1 text-sm text-muted-foreground">{t('localization.config.availableLocales')}</p>
        <div className="flex flex-wrap gap-1.5">
          {config.available_locales.map((c) => (
            <span key={c} className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-foreground">
              {c}
              <button
                type="button"
                aria-label={t('common.delete')}
                onClick={() => save({ available_locales: config.available_locales.filter((x) => x !== c) })}
                className="text-muted-foreground hover:text-destructive"
              >
                ✕
              </button>
            </span>
          ))}
        </div>
        <div className="mt-1.5 flex gap-1.5">
          <input
            type="text"
            value={addLocale}
            onChange={(e) => setAddLocale(e.target.value)}
            placeholder="en-US"
            className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={!addLocale.trim()}
            onClick={() => {
              save({ available_locales: [...config.available_locales, addLocale.trim()] });
              setAddLocale('');
            }}
          >
            {t('common.add')}
          </Button>
        </div>
      </div>

      <div>
        <p className="mb-1 text-sm text-muted-foreground">{t('localization.config.fallbackChain')}</p>
        <p className="mb-1.5 text-xs text-muted-foreground">{t('localization.config.fallbackChainHint')}</p>
        <ol className="space-y-1">
          {config.fallback_chain.map((c, i) => (
            <li key={`${c}-${i}`} className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5">
              <span className="w-5 text-xs text-muted-foreground">{i + 1}.</span>
              <span className="flex-1 text-sm text-foreground">{c}</span>
              <button
                type="button"
                disabled={i === 0}
                onClick={() => {
                  const next = [...config.fallback_chain];
                  [next[i - 1], next[i]] = [next[i], next[i - 1]];
                  save({ fallback_chain: next });
                }}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                aria-label={t('form.builder.moveUp')}
              >
                ▲
              </button>
              <button
                type="button"
                disabled={i === config.fallback_chain.length - 1}
                onClick={() => {
                  const next = [...config.fallback_chain];
                  [next[i], next[i + 1]] = [next[i + 1], next[i]];
                  save({ fallback_chain: next });
                }}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                aria-label={t('form.builder.moveDown')}
              >
                ▼
              </button>
              <button
                type="button"
                onClick={() => save({ fallback_chain: config.fallback_chain.filter((_, idx) => idx !== i) })}
                className="text-muted-foreground hover:text-destructive"
                aria-label={t('common.delete')}
              >
                ✕
              </button>
            </li>
          ))}
        </ol>
        <div className="mt-1.5 flex gap-1.5">
          <div className="w-40">
            <Select
              value={addFallback}
              onChange={(e) => setAddFallback(e.target.value)}
              placeholder={t('localization.config.addToChain')}
              options={availableLocales.filter((c) => !config.fallback_chain.includes(c)).map((c) => ({ value: c, label: c }))}
              aria-label={t('localization.config.addToChain')}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={!addFallback}
            onClick={() => {
              save({ fallback_chain: [...config.fallback_chain, addFallback] });
              setAddFallback('');
            }}
          >
            {t('common.add')}
          </Button>
        </div>
      </div>

      <ToggleSwitch label={t('localization.config.autoDetect')} checked={config.auto_detect} onChange={(v) => save({ auto_detect: v })} disabled={busy} />
      <ToggleSwitch label={t('localization.config.allowUserOverride')} checked={config.allow_user_override} onChange={(v) => save({ allow_user_override: v })} disabled={busy} />
    </div>
  );
}
